/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { observer } from 'mobx-react-lite'
import { useMobxStore } from '../../../state'
import Icon from '../../../components/Icon'
import LoadingSpinner from '../../../components/LoadingSpinner'
import colors from '@sportninja/common/constants/appColors'
import { EventCard } from './ConfirmProfile'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { Input } from './NewPlayerRegistration'
import { useCallback, useEffect, useState } from 'react'
import req from '@sportninja/common/api/request'
import { Button } from 'src/components/Button'

async function convertBase64(file) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()
    fileReader.readAsDataURL(file)
    fileReader.onload = () => {
      resolve(fileReader.result)
    }
    fileReader.onerror = (error) => {
      reject(error)
    }
  })
}

async function getSports() {
  const res = await req('/sports')

  return res.data
}

async function createTeam({ name, sportId, scheduleId }) {
  const res = await req('/teams/registration', {
    method: 'POST',
    body: JSON.stringify({
      sport_id: sportId,
      name: name,
      schedule_id: scheduleId,
      is_public: true,
      is_visible: true,
    }),
  })

  return res.data
}

async function updateTeamLogo(teamId, base64) {
  const res = await req(`/teams/${teamId}/image`, {
    method: 'POST',
    body: base64,
  })

  return res
}

export const TeamCreation = observer(({ isDarkMode }) => {
  const [teamName, setTeamName] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)
  const [sports, setSports] = useState(null)
  const [error, setError] = useState(null)
  const [disabled, setDisabled] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const {
    registrationInvite: {
      pushToStep,
      confirmTeamRepStep,
      setNewTeamId,
      state: { eventInfo },
    },
  } = useMobxStore()

  const onSubmit = useCallback(async () => {
    setError(null)
    setIsLoading(true)
    try {
      const res = await createTeam({
        name: teamName,
        sportId: eventInfo?.sport_id || sports[0].id,
        scheduleId: eventInfo?.id,
      })

      if (selectedFile) {
        const base64 = await convertBase64(selectedFile)

        await updateTeamLogo(res.id, base64)
      }

      setNewTeamId(res.id)
      setSuccess(true)
    } catch (e) {
      setSuccess(false)
      console.error(e)
      setError(e)
      setIsLoading(false)
    }
  }, [teamName, selectedFile, sports])

  useEffect(() => {
    getSports().then((res) => {
      setSports(res)
    })
  }, [])

  useEffect(() => {
    if (teamName) {
      setDisabled(false)
    } else {
      setDisabled(true)
    }
  }, [teamName])

  if (error && !error?.message?.includes('already exists')) {
    return (
      <div>
        <div
          css={css`
            margin-top: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <button onClick={() => pushToStep(1)}>
            <Icon
              name='arrow-left'
              color={colors.DEFAULT_FLAIR}
              fontSize={20}
            />
          </button>
        </div>
        <div>
          <p>{JSON.stringify(error)}</p>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div>
        <div
          css={css`
            margin-top: 1rem;
            margin-bottom: 1rem;
          `}
        ></div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            margin-top: 10rem;
            flex: 1;
            justify-content: center;
            align-items: center;
          `}
        >
          <h1
            css={css`
              text-align: center;
              margin-bottom: 32px;
              font-size: 34px;
              font-weight: 700;
              line-height: 1.2;
              color: ${isDarkMode ? colors.WHITE : 'black'};
              font-family: ${isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'Rift, Arial, Helvetica, sans-serif'};
            `}
          >
            Team
            <br />
            {teamName || ''}
            <br />
            Created!
          </h1>
          <Button
            onClick={() => confirmTeamRepStep({ scheduleId: eventInfo?.id })}
            label='Continue'
          />
        </div>
      </div>
    )
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        margin-top: 1rem;
        flex: 1;
        color: ${isDarkMode ? colors.WHITE : 'black'};
      `}
    >
      <div
        css={css`
          margin-bottom: 1rem;
        `}
      >
        <button
          onClick={() => pushToStep(1)}
          css={css`
            align-items: center;
            display: flex;
            gap: 16px;
          `}
        >
          <Icon name='chevron-left' color={colors.PALE_SKY} />
          <p
            css={css`
              color: ${colors.PALE_SKY};
              font-size: 16px;
              font-style: normal;
              font-weight: 500;
              line-height: normal;
            `}
          >
            Back
          </p>
        </button>
      </div>
      <div>
        <EventCard eventInfo={eventInfo} isDarkMode={isDarkMode} />
        <h1
          css={css`
            text-align: left;
            margin-top: 32px;
            font-size: 34px;
            font-weight: 700;
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
              : 'Rift, Arial, Helvetica, sans-serif'};
          `}
        >
          NEW TEAM
        </h1>
      </div>

      <div>
        <Input
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          label='Team Name'
          isDarkMode={isDarkMode}
          hasError={error?.message?.includes('already exists')}
        />
      </div>
      {error && (
        <div
          css={css`
            color: red;
            margin-bottom: 16px;
          `}
        >
          This team name is already in use. Please choose another.
        </div>
      )}
      <div
        css={css`
          margin-top: 10px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            flex: 1;

            input {
              display: inline;
              appearance: none;
              opacity: 0;
              width: 1px;
            }
          `}
        >
          <div
            css={css`
              font-size: 14px;
              margin-bottom: 8px;
            `}
          >
            Select the Team Logo
          </div>
          <div
            css={css`
              display: flex;
            `}
          >
            <label
              htmlFor='team-logo'
              css={css`
                display: flex;
                flex: 1;
                align-items: center;
                justify-content: space-between;
                padding: 8px 16px;
                height: 40px;
                background-color: ${isDarkMode ? '#33363B' : '#f5f5f5'};
                border: 1px solid ${colors.ATTENDANCE_GRAY};
                border-radius: 4px;
                cursor: pointer;
                color: ${isDarkMode ? colors.WHITE : colors.SOFT_STEEL};

                transition: border-color 0.15s ease-in-out;

                i {
                  transition: color 0.15s ease-in-out;
                }

                &:hover {
                  border-color: ${colors.BLACK_PEARL};

                  i {
                    color: ${colors.BLACK_PEARL};
                  }
                }
              `}
            >
              {selectedFile ? selectedFile.name : 'Upload Team Logo'}
              <Icon
                name='file-upload'
                color={isDarkMode ? colors.DEFAULT_FLAIR : colors.SOFT_STEEL}
                fontSize={16}
              />
            </label>
            <input
              type='file'
              id='team-logo'
              onChange={(e) => setSelectedFile(e.target.files[0])}
            />
          </div>
        </div>
      </div>
      <button
        disabled={disabled || isLoading}
        onClick={onSubmit}
        css={css`
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
          width: 100%;
          margin-top: 50px;
          border: 1px solid ${colors.DEFAULT_FLAIR};
          color: ${colors.DEFAULT_FLAIR};
          height: 50px;
          transition: background-color 0.15s ease-in-out,
            color 0.15s ease-in-out;
          font-size: 16px;
          font-weight: 700;
          opacity: ${disabled ? 0.5 : 1};

          &:hover {
            ${!disabled &&
            `
            background-color: ${colors.DEFAULT_FLAIR};
            color: ${colors.WHITE};
            `}
          }
        `}
      >
        {isLoading ? <LoadingSpinner size={2} /> : 'Create Team'}
      </button>
    </div>
  )
})
